import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { PrimeNgModule } from '@shared/prime-ng/prime-ng.module';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { IDropdowns } from '@core/models';
import { DateService } from '@core/services/date.service';
import { HcpcsClinicalFormItem } from '@feature/models/code-management.model';
import { MfeCoreModule } from '@mynexus/mfe-core';

@Component({
  selector: 'app-clinical-questionnaire-forms',
  templateUrl: './clinical-questionnaire-forms.component.html',
  styleUrls: ['./clinical-questionnaire-forms.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNgModule,
    MfeCoreModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [ConfirmationService, MessageService]
})
export class ClinicalQuestionnaireFormsComponent implements OnInit, OnChanges {
  @Input() mode: 'add' | 'edit' | 'view' = 'add';
  @Input() isVisible: boolean = true;
  @Input() initialForms?: HcpcsClinicalFormItem[];
  @Input() cmsEffectiveStartDate?: Date | null;
  @Input() planEffectiveStartDate?: Date | null;
  @Input() hcpcsByTenantLoBProductId?: string; 
  @Input() hcpcsCode?: string; 
  @Input() tenantId?: string;
  @Input() isLoading: boolean = false;
  @Output() initialLoadComplete = new EventEmitter<void>();
  
  formsFormGroup!: FormGroup;
  
  readonly isSharedFormOptions: IDropdowns[] = [
    { label: 'Select', value: '' },
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' }
  ];

  formTitleDropdowns: IDropdowns[] = [];
  filteredFormTitles: string[] = [];
  
  // Store original values before editing for cancel functionality
  private originalFormValues: Map<number, any> = new Map();
  
  private readonly DEFAULT_FORM_NAME = 'DMEPOS-No Auto Adjudication Clinical Question';
  private allFormsLoaded = false;
  
  showPreviewDialog = false;
  previewFormId = '';
  previewFormTitle = '';
  formLoaded = false;
  formData: any = null;
  formSubmission: any = {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly codeManagementApiService: CodeManagementApiService,
    public readonly cdr: ChangeDetectorRef,
    private readonly dateService: DateService
  ) {}

  ngOnInit(): void {
    this.buildFormsFormGroup();
    this.loadInitialForms();
    if (this.initialForms !== undefined && this.initialForms.length === 0) {
      this.addDefaultFormIfNeeded();
    }
    
    this.initialLoadComplete.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialForms'] && !changes['initialForms'].firstChange && this.mode === 'view') {
      if (!this.formsFormGroup) {
        return;
      }
      this.formsList.clear();
      this.loadInitialForms();
      this.cdr.markForCheck();
    }

    if (this.mode !== 'view' && (changes['planEffectiveStartDate'] || changes['cmsEffectiveStartDate'])) {
      if (!this.formsFormGroup) {
        return;
      }
      
      const newformEffectiveStartDate = this.planEffectiveStartDate ?? this.cmsEffectiveStartDate ?? null;
      
      this.formsList.controls.forEach((control) => {
        const formGroup = control as FormGroup;
        if (formGroup.get('isEditing')?.value && formGroup.get('isFormDateLocked')?.value) {
          formGroup.patchValue({ formEffectiveStartDate: newformEffectiveStartDate }, { emitEvent: false });
        }
      });
    }
  }

  private buildFormsFormGroup(): void {
    this.formsFormGroup = this.fb.group({
      formsList: this.fb.array([])
    });
  }

  get formsList(): FormArray {
    return this.formsFormGroup.get('formsList') as FormArray;
  }

  private loadInitialForms(): void {
    if (this.initialForms && this.initialForms.length > 0) {
      this.initialForms.forEach(form => {
        this.addFormWithData(form);
      });
    }
  }


  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  addForm(): void {
    const formGroup = this.createFormGroup(true);
    this.formsList.push(formGroup);
  }

  private addFormWithData(formData: HcpcsClinicalFormItem): void {
    const formGroup = this.createFormGroup(false);
    formGroup.patchValue({
      id: formData.id,
      associatedFormId: formData.associatedFormId,
      associatedFormTitle: formData.associatedFormTitle,
      sharedForm: formData.sharedForm,
      formEffectiveStartDate: formData.formEffectiveStartDate,
      isNewClinicalForm: formData.isNew ?? false,
      isEditing: formData.isEditing ?? false
    });
    this.formsList.push(formGroup);
  }

  getFormGroup(index: number): FormGroup {
    return this.formsList.at(index) as FormGroup;
  }

  saveForm(index: number): void {
    const formGroup = this.getFormGroup(index);
    
    if (formGroup.invalid) {
      formGroup.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Required Field Missing',
        detail: 'To proceed complete the missing info first'
      });
      return;
    }

    const isNewClinicalForm = formGroup.get('isNewClinicalForm')?.value;
    
    formGroup.patchValue({
      isEditing: false,
      isNewClinicalForm: false
    });
    
    this.originalFormValues.delete(index);

    this.messageService.add({
      severity: 'success',
      summary: isNewClinicalForm ? 'Clinical Questionnaire Form(s) Added' : 'Clinical Questionnaire Form(s) Saved',
      detail: isNewClinicalForm ? 'Clinical Questionnaire Form(s) was successfully added' : 'Clinical Questionnaire Form(s) was successfully saved'
    });
  }

  cancelForm(index: number): void {
    const formGroup = this.getFormGroup(index);
    const isNewClinicalForm = formGroup.get('isNewClinicalForm')?.value;
    
    if (isNewClinicalForm) {
      this.removeForm(index);
      return;
    }
    
    const originalValues = this.originalFormValues.get(index);
    if (originalValues) {
      formGroup.patchValue({...originalValues, isEditing: false});
      this.originalFormValues.delete(index);
    } else {
      formGroup.patchValue({isEditing: false});
    }
  }

  private removeForm(index: number): void {
    this.formsList.removeAt(index);
  }

  editForm(index: number): void {
    const formGroup = this.getFormGroup(index);
    
    this.originalFormValues.set(index, {
      id: formGroup.get('id')?.value,
      associatedFormId: formGroup.get('associatedFormId')?.value,
      associatedFormTitle: formGroup.get('associatedFormTitle')?.value,
      sharedForm: formGroup.get('sharedForm')?.value,
      formEffectiveStartDate: formGroup.get('formEffectiveStartDate')?.value,
      isFormDateLocked: formGroup.get('isFormDateLocked')?.value
    });
    
    formGroup.patchValue({
      isEditing: true
    });
  }

  deleteForm(index: number): void {
    this.removeForm(index);
  }

  previewForm(index: number): void {
    const formGroup = this.getFormGroup(index);
    this.previewFormId = formGroup.get('associatedFormId')?.value || '';
    this.previewFormTitle = formGroup.get('associatedFormTitle')?.value || '';
    this.showPreviewDialog = true;
    this.formLoaded = false;
    this.formData = null;
    
    if (this.previewFormId && this.tenantId) {
      this.codeManagementApiService.getForm(this.previewFormId, this.tenantId).subscribe({
        next: (formData) => {
          if (formData?.['components']) {
            for (let i = 0; i < formData['components'].length; i++) {
              if (formData['components'][i].key === 'submit') {
                formData['components'][i].hidden = true;
              }
            }
          }
          
          this.formData = formData;
          this.formSubmission = {};
          this.formLoaded = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load form preview'
          });
          this.formLoaded = true;
          this.cdr.detectChanges();
        }
      });
    }
  }
  
  closePreviewDialog(): void {
    this.showPreviewDialog = false;
    this.previewFormId = '';
    this.previewFormTitle = '';
    this.formLoaded = false;
    this.formData = null;
  }

  isRowEditable(index: number): boolean {
    const formGroup = this.getFormGroup(index);
    return formGroup.get('isEditing')?.value ?? false;
  }

  addDefaultFormIfNeeded(): void {
    if (this.formsList.length > 0) return;
    
    if (!this.planEffectiveStartDate && !this.cmsEffectiveStartDate) return;
    
    this.codeManagementApiService.searchClinicalForms(this.DEFAULT_FORM_NAME).subscribe({
      next: (forms) => {
        const form = forms.find(f => f.formIoName === this.DEFAULT_FORM_NAME);
        if (form) {
          const formGroup = this.createFormGroup(false);
          formGroup.patchValue({
            associatedFormId: form.formIoId,
            associatedFormTitle: form.formIoName,
            sharedForm: 'Yes',
            formEffectiveStartDate: this.getEffectiveDate(),
            isNewClinicalForm: false,
            isEditing: false,
            isFormDateLocked: true
          });
          this.formsList.push(formGroup);
          this.cdr.markForCheck();
        }
      },
      error: () => {}
    });
  }
  
  private loadAllForms(): void {
    if (this.allFormsLoaded) return;
    
    this.codeManagementApiService.searchClinicalForms('').subscribe({
      next: (forms) => {
        this.formTitleDropdowns = forms.map(f => ({ label: f.formIoName, value: f.formIoId }));
        this.allFormsLoaded = true;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load clinical forms'
        });
      }
    });
  }


  onSearchFormTitles(event: { query: string }): void {
    if (!this.allFormsLoaded) this.loadAllForms();
    
    const query = event.query.toLowerCase();
    this.filteredFormTitles = this.formTitleDropdowns
      .filter(item => item.label?.toLowerCase().includes(query))
      .map(item => item.label);
  }

  applyFormSelection(selectedTitle: string, index: number): void {
    const formGroup = this.getFormGroup(index);
    
    if (selectedTitle) {
      const selectedForm = this.formTitleDropdowns.find(f => f.label === selectedTitle);
      if (selectedForm) {
        formGroup.patchValue({
          associatedFormId: selectedForm.value,
          associatedFormTitle: selectedForm.label
        });
      }
    } else {
      formGroup.patchValue({
        associatedFormId: '',
        associatedFormTitle: ''
      });
    }
    this.cdr.markForCheck();
  }

  getFormTitleDisplay(index: number): string {
    return this.getFormGroup(index).get('associatedFormTitle')?.value || '';
  }

  toggleFormDateLock(index: number): void {
    const formGroup = this.getFormGroup(index);
    const isFormEffectiveDateCurrentlyLocked = formGroup.get('isFormDateLocked')?.value;
    
    formGroup.patchValue({
      isFormDateLocked: !isFormEffectiveDateCurrentlyLocked
    });

    if (!isFormEffectiveDateCurrentlyLocked) {
      const effectiveDate = this.getEffectiveDate();
      formGroup.patchValue({
        formEffectiveStartDate: effectiveDate
      });
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'Effective Dates Unlocked',
        detail: 'You can proceed with editing'
      });
    }
  }

  isFormDateLocked(index: number): boolean {
    const formGroup = this.getFormGroup(index);
    return formGroup.get('isFormDateLocked')?.value ?? true;
  }

  getFormsData(): any[] {
    return this.formsList.controls
      .filter(control => !control.get('isEditing')?.value)
      .map(control => {
        const dateValue = control.get('formEffectiveStartDate')?.value;
        return {
          hcpcsToFormId: control.get('id')?.value || undefined,
          formIoId: control.get('associatedFormId')?.value,
          isSharedForm: control.get('sharedForm')?.value === 'Yes',
          effectiveStartDate: dateValue ? this.dateService.transformPOSTDate(dateValue) : null,
          hcpcsByTenantLoBProductId: this.hcpcsByTenantLoBProductId
        };
      });
  }

  validateForms(): boolean {
    const forms = this.formsList.controls;
    return forms.every(control => {
      const formId = control.get('associatedFormId')?.value;
      const sharedForm = control.get('sharedForm')?.value;
      const effectiveDate = control.get('formEffectiveStartDate')?.value;
      return formId && sharedForm && effectiveDate;
    });
  }

  private getEffectiveDate(): Date {
    if (this.planEffectiveStartDate) {
      return this.planEffectiveStartDate;
    }
    if (this.cmsEffectiveStartDate) {
      return this.cmsEffectiveStartDate;
    }
    return new Date();
  }

  private createFormGroup(isNewClinicalForm: boolean = true): FormGroup {
    const defaultStartDate = this.getEffectiveDate();
    
    return this.fb.group({
      id: [null],
      associatedFormId: ['', Validators.required],
      associatedFormTitle: ['', Validators.required],
      sharedForm: ['', Validators.required],
      formEffectiveStartDate: [defaultStartDate, Validators.required],
      isNewClinicalForm: [isNewClinicalForm],
      isEditing: [isNewClinicalForm],
      isFormDateLocked: [true]
    });
  }

}
